<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'users' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">{{ user.name || 'Détails de l\'utilisateur' }}</h1>
          <p v-if="user.id" class="text-xs text-gray-400">
            Membre depuis {{ formatDate(user.created_at) }}
          </p>
        </div>
      </div>
      <div v-if="user.id" class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="showEditModal = true">
          <i class="fas fa-pen text-[11px]"></i>
          Modifier
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading"
      class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement...</p>
    </div>

    <!-- Utilisateur introuvable -->
    <div v-else-if="!user.id"
      class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Utilisateur introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">L'utilisateur que vous recherchez n'existe pas.</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'users' })">
        Retour aux utilisateurs
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte infos utilisateur -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div class="flex items-center gap-4">
              <div class="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span class="text-lg font-bold text-primary">{{ getInitials(user.name) }}</span>
              </div>
              <div>
                <h2 class="text-base font-bold text-gray-900 uppercase">{{ user.name }}</h2>
                <p class="text-xs text-gray-400 mt-0.5">{{ user.email }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span :class="[
                'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold',
                user.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
              ]">
                {{ user.role === 'admin' ? 'Administrateur' : 'Utilisateur' }}
              </span>
              <span :class="[
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
                user.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
              ]">
                <span class="w-1.5 h-1.5 rounded-full" :class="user.active ? 'bg-green-500' : 'bg-gray-400'"></span>
                {{ user.active ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Email</p>
              <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <i class="fas fa-envelope text-[9px] text-gray-300"></i>
                {{ user.email }}
              </p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Rôle</p>
              <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <i class="fas fa-shield-halved text-[9px] text-gray-300"></i>
                {{ user.role === 'admin' ? 'Administrateur' : 'Utilisateur standard' }}
              </p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Date d'ajout</p>
              <p class="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <i class="fas fa-calendar text-[9px] text-gray-300"></i>
                {{ formatDate(user.created_at) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Section Permissions -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-semibold text-gray-700">Permissions</h3>
              <span v-if="user.role !== 'admin' && !permissionsLoading" class="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                {{ selectedPermissions.length }}/{{ availablePermissions.length }}
              </span>
            </div>
            <div v-if="user.role !== 'admin'" class="flex items-center gap-2">
              <button
                v-if="hasPermissionChanges"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                @click="resetPermissions"
                :disabled="savingPermissions">
                Annuler
              </button>
              <button
                v-if="hasPermissionChanges"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
                @click="savePermissions"
                :disabled="savingPermissions">
                <i v-if="savingPermissions" class="fas fa-spinner fa-spin text-[10px]"></i>
                <span>Enregistrer</span>
              </button>
            </div>
          </div>

          <!-- Admin: toutes les permissions -->
          <div v-if="user.role === 'admin'" class="p-5">
            <div class="flex items-center gap-3 px-4 py-3 bg-purple-50 border border-purple-100 rounded-lg">
              <i class="fas fa-crown text-purple-500 text-sm"></i>
              <div>
                <p class="text-sm font-medium text-purple-700">Accès complet</p>
                <p class="text-xs text-purple-500">Les administrateurs ont accès à toutes les fonctionnalités sans restriction.</p>
              </div>
            </div>
          </div>

          <!-- Utilisateur standard: gestion des permissions -->
          <div v-else class="p-5">
            <!-- Chargement -->
            <div v-if="permissionsLoading" class="flex items-center justify-center py-8">
              <div class="w-6 h-6 border-[2px] border-gray-200 border-t-primary rounded-full animate-spin mr-2"></div>
              <span class="text-sm text-gray-400">Chargement des permissions...</span>
            </div>

            <!-- Contenu permissions -->
            <div v-else>
              <!-- Barre d'actions : recherche + actions globales -->
              <div class="flex flex-wrap items-center gap-2 mb-4">
                <div class="relative flex-1 min-w-[180px]">
                  <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                  <input type="text" v-model="permissionSearch" placeholder="Rechercher une permission..."
                    class="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
                </div>
                <div class="flex items-center gap-1.5">
                  <button
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    @click="selectAllPermissions"
                    title="Tout sélectionner">
                    <i class="fas fa-check-double text-[9px]"></i>
                    Tout
                  </button>
                  <button
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    @click="deselectAllPermissions"
                    title="Tout désélectionner">
                    <i class="fas fa-xmark text-[9px]"></i>
                    Aucun
                  </button>
                  <button
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    @click="showClearAllModal = true"
                    title="Retirer toutes les permissions">
                    <i class="fas fa-ban text-[9px]"></i>
                    Réinitialiser
                  </button>
                </div>
              </div>

              <!-- Résumé des changements en attente -->
              <div v-if="hasPermissionChanges" class="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <div class="flex items-start gap-2">
                  <i class="fas fa-exclamation-triangle text-amber-500 text-xs mt-0.5 flex-shrink-0"></i>
                  <div class="flex-1">
                    <p class="text-xs font-medium text-amber-700 mb-1">Modifications non enregistrées</p>
                    <div class="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                      <span v-if="addedPermissions.length > 0" class="text-green-600">
                        <i class="fas fa-plus text-[8px] mr-0.5"></i>{{ addedPermissions.length }} ajoutée(s)
                      </span>
                      <span v-if="removedPermissions.length > 0" class="text-red-600">
                        <i class="fas fa-minus text-[8px] mr-0.5"></i>{{ removedPermissions.length }} retirée(s)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Liste des permissions par groupe -->
              <div class="space-y-3">
                <div v-for="group in filteredPermissionGroups" :key="group.name"
                  class="border rounded-lg overflow-hidden transition-colors"
                  :class="hasGroupChanges(group) ? 'border-amber-200' : 'border-gray-100'">
                  <!-- En-tête du groupe -->
                  <div class="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 cursor-pointer"
                    @click="toggleGroup(group.name)">
                    <div class="flex items-center gap-2">
                      <i :class="group.icon" class="text-xs text-gray-400 w-4 text-center"></i>
                      <span class="text-xs font-semibold text-gray-600 uppercase tracking-wider">{{ group.label }}</span>
                      <span class="text-[10px] text-gray-400">({{ getGroupActiveCount(group) }}/{{ group.permissions.length }})</span>
                      <span v-if="hasGroupChanges(group)" class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                    </div>
                    <div class="flex items-center gap-3">
                      <label class="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer" @click.stop>
                        <input type="checkbox" class="w-3.5 h-3.5 text-primary border-gray-300 rounded focus:ring-primary/20"
                          :checked="isGroupFullyChecked(group)"
                          :indeterminate="isGroupPartiallyChecked(group)"
                          @change="toggleGroupPermissions(group, $event.target.checked)" />
                        Tout
                      </label>
                      <i :class="expandedGroups.includes(group.name) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                        class="text-[10px] text-gray-300"></i>
                    </div>
                  </div>

                  <!-- Permissions du groupe -->
                  <div v-show="expandedGroups.includes(group.name)" class="px-4 py-3">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      <label v-for="perm in group.permissions" :key="perm.value"
                        class="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all"
                        :class="getPermissionItemClass(perm.value)">
                        <input type="checkbox" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary/20"
                          :checked="selectedPermissions.includes(perm.value)"
                          @change="togglePermission(perm.value)" />
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-1.5">
                            <p class="text-sm text-gray-700 truncate">{{ perm.label }}</p>
                            <span v-if="isPermissionAdded(perm.value)"
                              class="flex-shrink-0 text-[9px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                              AJOUTÉE
                            </span>
                            <span v-else-if="isPermissionRemoved(perm.value)"
                              class="flex-shrink-0 text-[9px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                              RETIRÉE
                            </span>
                          </div>
                          <p v-if="perm.description" class="text-[11px] text-gray-400 truncate">{{ perm.description }}</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Aucun résultat de recherche -->
                <div v-if="permissionSearch && filteredPermissionGroups.length === 0" class="text-center py-6">
                  <i class="fas fa-search text-gray-300 text-lg mb-2"></i>
                  <p class="text-sm text-gray-400">Aucune permission ne correspond à "{{ permissionSearch }}"</p>
                </div>

                <!-- État vide -->
                <div v-if="!permissionSearch && permissionGroups.length === 0" class="text-center py-8">
                  <i class="fas fa-lock text-gray-300 text-2xl mb-2"></i>
                  <p class="text-sm text-gray-400">Aucune permission disponible</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Statistiques -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Statistiques</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-cart-shopping text-blue-500 text-[10px]"></i>
                </span>
                Nombre de ventes
              </span>
              <span class="text-sm font-bold text-gray-900">{{ user.statistics?.sales_count || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-coins text-green-500 text-[10px]"></i>
                </span>
                Total des ventes
              </span>
              <span class="text-sm font-bold text-green-600">{{ formatPrice(user.statistics?.sales_total || 0) }}</span>
            </div>
          </div>
        </div>

        <!-- Statut de l'utilisateur -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-gray-700">Statut</h3>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ user.active ? 'L\'utilisateur est actif' : 'L\'utilisateur est inactif' }}
              </p>
            </div>
            <button
              class="relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none"
              :class="user.active ? 'bg-primary' : 'bg-gray-300'"
              :disabled="togglingStatus"
              @click="showStatusConfirmModal = true">
              <span
                class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                :class="user.active ? 'translate-x-5' : 'translate-x-0'"></span>
            </button>
          </div>
        </div>

        <!-- Actions -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
          <div class="space-y-1.5">
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="showEditModal = true">
              <i class="fas fa-pen text-xs w-4 text-center"></i>
              Modifier les informations
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="showRoleModal = true">
              <i class="fas fa-shield-halved text-xs w-4 text-center"></i>
              Changer le rôle
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              @click="showDeleteModal = true"
              :disabled="user.active && user.statistics && user.statistics.sales_count > 0">
              <i class="fas fa-trash text-xs w-4 text-center"></i>
              Supprimer l'utilisateur
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal confirmation réinitialiser permissions -->
    <transition name="modal">
      <div v-if="showClearAllModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="showClearAllModal = false">
        <div class="bg-white rounded-xl max-w-sm w-full shadow-xl p-6 text-center" @click.stop>
          <div class="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-50">
            <i class="fas fa-ban text-red-500 text-2xl"></i>
          </div>
          <h3 class="text-base font-bold text-gray-900 mb-2">Retirer toutes les permissions ?</h3>
          <p class="text-sm text-gray-500 mb-2 leading-relaxed">
            Cette action va retirer toutes les permissions de "{{ user.name }}".
          </p>
          <p class="text-xs text-gray-400 mb-6">
            L'utilisateur n'aura plus accès à aucune fonctionnalité de l'application.
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              :disabled="savingPermissions"
              @click="showClearAllModal = false">
              Annuler
            </button>
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
              :disabled="savingPermissions"
              @click="clearAllPermissions">
              <i v-if="savingPermissions" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
              Confirmer
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal modifier utilisateur -->
    <transition name="modal">
      <div v-if="showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="showEditModal = false">
        <div class="bg-white rounded-xl max-w-lg w-full shadow-xl" @click.stop>
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-900">Modifier l'utilisateur</h3>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="showEditModal = false">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
          <form @submit.prevent="saveUser" class="p-6 space-y-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nom complet <span class="text-red-500">*</span></p>
              <input type="text" v-model="editForm.name" required
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Email <span class="text-red-500">*</span></p>
              <input type="email" v-model="editForm.email" required
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div class="flex gap-3 pt-2">
              <button type="button"
                class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="showEditModal = false">
                Annuler
              </button>
              <button type="submit"
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="saving">
                <i v-if="saving" class="fas fa-spinner fa-spin text-[10px]"></i>
                <span v-else>Enregistrer</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Modal changer le rôle -->
    <transition name="modal">
      <div v-if="showRoleModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="showRoleModal = false">
        <div class="bg-white rounded-xl max-w-sm w-full shadow-xl p-6 text-center" @click.stop>
          <div class="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-purple-50">
            <i class="fas fa-shield-halved text-purple-500 text-2xl"></i>
          </div>
          <h3 class="text-base font-bold text-gray-900 mb-2">Changer le rôle</h3>
          <p class="text-sm text-gray-500 mb-5">
            {{ user.role === 'admin'
              ? `Retirer les droits d'administrateur à "${user.name}" ?`
              : `Promouvoir "${user.name}" en tant qu'administrateur ?`
            }}
          </p>
          <div v-if="user.role !== 'admin'" class="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg mb-5 text-left">
            <i class="fas fa-exclamation-triangle text-amber-500 text-xs flex-shrink-0"></i>
            <span class="text-xs text-amber-700">Un administrateur a accès complet à toutes les fonctionnalités et ne nécessite pas de permissions spécifiques.</span>
          </div>
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              :disabled="saving"
              @click="showRoleModal = false">
              Annuler
            </button>
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              :class="user.role === 'admin' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-purple-600 hover:bg-purple-700'"
              :disabled="saving"
              @click="changeRole">
              <i v-if="saving" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
              {{ user.role === 'admin' ? 'Retirer admin' : 'Promouvoir admin' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal confirmation statut -->
    <transition name="modal">
      <div v-if="showStatusConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="showStatusConfirmModal = false">
        <div class="bg-white rounded-xl max-w-sm w-full shadow-xl p-6 text-center" @click.stop>
          <div class="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
            :class="user.active ? 'bg-amber-50' : 'bg-green-50'">
            <i :class="user.active ? 'fas fa-exclamation-circle text-amber-500' : 'fas fa-check-circle text-green-500'"
              class="text-2xl"></i>
          </div>
          <h3 class="text-base font-bold text-gray-900 mb-2">
            {{ user.active ? 'Désactiver l\'utilisateur ?' : 'Activer l\'utilisateur ?' }}
          </h3>
          <p class="text-sm text-gray-500 mb-6 leading-relaxed">
            {{ user.active
              ? `"${user.name}" ne pourra plus se connecter à l'application.`
              : `"${user.name}" pourra de nouveau se connecter à l'application.`
            }}
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              :disabled="togglingStatus"
              @click="showStatusConfirmModal = false">
              Annuler
            </button>
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              :class="user.active ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary-dark'"
              :disabled="togglingStatus"
              @click="toggleStatus">
              <i v-if="togglingStatus" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
              {{ user.active ? 'Désactiver' : 'Activer' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal confirmation suppression -->
    <transition name="modal">
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="showDeleteModal = false">
        <div class="bg-white rounded-xl max-w-sm w-full shadow-xl p-6 text-center" @click.stop>
          <div class="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-50">
            <i class="fas fa-trash text-red-500 text-2xl"></i>
          </div>
          <h3 class="text-base font-bold text-gray-900 mb-2">Supprimer l'utilisateur ?</h3>
          <p class="text-sm text-gray-500 mb-6 leading-relaxed">
            Cette action est irréversible. L'utilisateur "{{ user.name }}" sera définitivement supprimé.
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              :disabled="saving"
              @click="showDeleteModal = false">
              Annuler
            </button>
            <button
              class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
              :disabled="saving"
              @click="deleteUser">
              <i v-if="saving" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import usersService from '@/services/usersService'
import permissionsService from '@/services/permissionsService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'UserDetailView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    // State
    const user = ref({})
    const loading = ref(true)
    const saving = ref(false)
    const togglingStatus = ref(false)

    // Modals
    const showEditModal = ref(false)
    const showRoleModal = ref(false)
    const showStatusConfirmModal = ref(false)
    const showDeleteModal = ref(false)
    const showClearAllModal = ref(false)

    // Edit form
    const editForm = reactive({ name: '', email: '' })

    // Permissions
    const permissionsLoading = ref(false)
    const savingPermissions = ref(false)
    const availablePermissions = ref([])
    const userPermissions = ref([])
    const selectedPermissions = ref([])
    const expandedGroups = ref([])
    const permissionSearch = ref('')

    // Permission groups definition
    const permissionGroupsDef = [
      {
        name: 'dashboard',
        label: 'Tableau de bord',
        icon: 'fas fa-chart-line',
        keys: ['dashboard']
      },
      {
        name: 'sales',
        label: 'Ventes',
        icon: 'fas fa-cart-shopping',
        keys: ['sales', 'make-sale']
      },
      {
        name: 'sale-invoices',
        label: 'Factures clients',
        icon: 'fas fa-file-invoice',
        keys: ['sale-invoices']
      },
      {
        name: 'payments',
        label: 'Paiements',
        icon: 'fas fa-credit-card',
        keys: ['payments']
      },
      {
        name: 'cash-registers',
        label: 'Caisses',
        icon: 'fas fa-cash-register',
        keys: ['cash-registers']
      },
      {
        name: 'products',
        label: 'Produits',
        icon: 'fas fa-box',
        keys: ['products']
      },
      {
        name: 'categories',
        label: 'Catégories',
        icon: 'fas fa-tags',
        keys: ['categories']
      },
      {
        name: 'inventory',
        label: 'Inventaire & Stock',
        icon: 'fas fa-warehouse',
        keys: ['inventory', 'inventory-transactions', 'physical-inventories', 'stock-withdrawals', 'product-regularizations']
      },
      {
        name: 'customers',
        label: 'Clients',
        icon: 'fas fa-users',
        keys: ['customers']
      },
      {
        name: 'suppliers',
        label: 'Fournisseurs',
        icon: 'fas fa-truck',
        keys: ['suppliers']
      },
      {
        name: 'purchase-orders',
        label: 'Commandes fournisseurs',
        icon: 'fas fa-clipboard-list',
        keys: ['purchase-orders']
      },
      {
        name: 'supplier-invoices',
        label: 'Factures fournisseurs',
        icon: 'fas fa-file-invoice-dollar',
        keys: ['supplier-invoices']
      },
      {
        name: 'users',
        label: 'Utilisateurs',
        icon: 'fas fa-user-gear',
        keys: ['users']
      },
      {
        name: 'companies',
        label: 'Entreprises',
        icon: 'fas fa-building',
        keys: ['companies']
      },
      {
        name: 'settings',
        label: 'Paramètres & Notifications',
        icon: 'fas fa-cog',
        keys: ['company-settings', 'notifications']
      }
    ]

    // Computed
    const permissionGroups = computed(() => {
      if (availablePermissions.value.length === 0) return []

      return permissionGroupsDef.map(group => {
        const perms = availablePermissions.value.filter(p => {
          return group.keys.some(key => p.value === key || p.value.startsWith(key + '.'))
        })
        return { ...group, permissions: perms }
      }).filter(group => group.permissions.length > 0)
    })

    const hasPermissionChanges = computed(() => {
      if (selectedPermissions.value.length !== userPermissions.value.length) return true
      const sorted1 = [...selectedPermissions.value].sort()
      const sorted2 = [...userPermissions.value].sort()
      return JSON.stringify(sorted1) !== JSON.stringify(sorted2)
    })

    const addedPermissions = computed(() => {
      return selectedPermissions.value.filter(p => !userPermissions.value.includes(p))
    })

    const removedPermissions = computed(() => {
      return userPermissions.value.filter(p => !selectedPermissions.value.includes(p))
    })

    const filteredPermissionGroups = computed(() => {
      if (!permissionSearch.value) return permissionGroups.value
      const query = permissionSearch.value.toLowerCase().trim()
      return permissionGroups.value
        .map(group => {
          const filteredPerms = group.permissions.filter(p =>
            p.label.toLowerCase().includes(query) ||
            p.value.toLowerCase().includes(query) ||
            (p.description && p.description.toLowerCase().includes(query))
          )
          return { ...group, permissions: filteredPerms }
        })
        .filter(group => group.permissions.length > 0 || group.label.toLowerCase().includes(query))
    })

    // Methods
    const loadUser = async () => {
      const userId = route.params.id
      if (!userId) {
        router.push({ name: 'users' })
        return
      }

      try {
        loading.value = true
        const response = await usersService.getUser(userId)
        console.clear()
        console.log('response:', response)
        user.value = response.data.user || response.data
        editForm.name = user.value.name
        editForm.email = user.value.email

        if (user.value.role !== 'admin') {
          loadPermissions()
        }
      } catch (error) {
        console.error('Erreur:', error)
        user.value = {}
      } finally {
        loading.value = false
      }
    }

    const loadPermissions = async () => {
      const userId = route.params.id
      if (!userId) return

      try {
        permissionsLoading.value = true

        const [availableRes, userPermsRes] = await Promise.all([
          permissionsService.getAvailablePermissions(),
          permissionsService.getUserPermissions(userId)
        ])

        // Parse available permissions - API returns { name: "Label", value: "key" }
        const rawAvailable = extractArray(availableRes.data)
        availablePermissions.value = rawAvailable.map(p => {
          if (typeof p === 'string') {
            return { value: p, label: formatPermissionLabel(p) }
          }
          const value = p.value || p.name || p
          const label = p.value ? (p.name || formatPermissionLabel(value)) : formatPermissionLabel(value)
          return { value, label, description: p.description || '' }
        })

        // Parse user permissions - handle various response formats
        const rawUserPerms = extractArray(userPermsRes.data)
        userPermissions.value = rawUserPerms.map(p => typeof p === 'string' ? p : (p.name || p.value || p))
        selectedPermissions.value = [...userPermissions.value]

        // Expand groups that have active permissions
        expandedGroups.value = permissionGroups.value
          .filter(g => g.permissions.some(p => selectedPermissions.value.includes(p.value)))
          .map(g => g.name)
      } catch (error) {
        console.error('Erreur lors du chargement des permissions:', error)
      } finally {
        permissionsLoading.value = false
      }
    }

    // Extract an array from various API response formats
    const extractArray = (data) => {
      if (Array.isArray(data)) return data
      if (data && typeof data === 'object') {
        // Try common response keys: data, permissions, items
        if (Array.isArray(data.data)) return data.data
        if (Array.isArray(data.permissions)) return data.permissions
        if (Array.isArray(data.items)) return data.items
        // If data.data is an object, look deeper
        if (data.data && typeof data.data === 'object') {
          if (Array.isArray(data.data.permissions)) return data.data.permissions
          if (Array.isArray(data.data.data)) return data.data.data
        }
      }
      return []
    }

    const formatPermissionLabel = (key) => {
      if (!key) return ''
      // Fallback: transform permission key into readable label
      return key.replace(/[-_.]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }

    const toggleGroup = (groupName) => {
      const idx = expandedGroups.value.indexOf(groupName)
      if (idx >= 0) {
        expandedGroups.value.splice(idx, 1)
      } else {
        expandedGroups.value.push(groupName)
      }
    }

    const togglePermission = (permValue) => {
      const idx = selectedPermissions.value.indexOf(permValue)
      if (idx >= 0) {
        selectedPermissions.value.splice(idx, 1)
      } else {
        selectedPermissions.value.push(permValue)
      }
    }

    const isGroupFullyChecked = (group) => {
      return group.permissions.every(p => selectedPermissions.value.includes(p.value))
    }

    const isGroupPartiallyChecked = (group) => {
      const checked = group.permissions.filter(p => selectedPermissions.value.includes(p.value)).length
      return checked > 0 && checked < group.permissions.length
    }

    const getGroupActiveCount = (group) => {
      return group.permissions.filter(p => selectedPermissions.value.includes(p.value)).length
    }

    const toggleGroupPermissions = (group, checked) => {
      group.permissions.forEach(p => {
        const idx = selectedPermissions.value.indexOf(p.value)
        if (checked && idx < 0) {
          selectedPermissions.value.push(p.value)
        } else if (!checked && idx >= 0) {
          selectedPermissions.value.splice(idx, 1)
        }
      })
    }

    const resetPermissions = () => {
      selectedPermissions.value = [...userPermissions.value]
    }

    const selectAllPermissions = () => {
      selectedPermissions.value = availablePermissions.value.map(p => p.value)
      // Expand all groups to show changes
      expandedGroups.value = permissionGroups.value.map(g => g.name)
    }

    const deselectAllPermissions = () => {
      selectedPermissions.value = []
    }

    const clearAllPermissions = async () => {
      try {
        savingPermissions.value = true
        await permissionsService.clearUserPermissions(user.value.id)
        userPermissions.value = []
        selectedPermissions.value = []
        showClearAllModal.value = false
        toastStore.addToast({ type: 'success', message: 'Toutes les permissions ont été retirées' })
      } catch (error) {
        console.error('Erreur lors de la suppression des permissions:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la suppression des permissions' })
      } finally {
        savingPermissions.value = false
      }
    }

    const isPermissionAdded = (permValue) => {
      return addedPermissions.value.includes(permValue)
    }

    const isPermissionRemoved = (permValue) => {
      return removedPermissions.value.includes(permValue)
    }

    const getPermissionItemClass = (permValue) => {
      if (isPermissionAdded(permValue)) return 'bg-green-50/60 hover:bg-green-50'
      if (isPermissionRemoved(permValue)) return 'bg-red-50/60 hover:bg-red-50'
      return 'hover:bg-gray-50'
    }

    const hasGroupChanges = (group) => {
      return group.permissions.some(p =>
        addedPermissions.value.includes(p.value) || removedPermissions.value.includes(p.value)
      )
    }

    const savePermissions = async () => {
      try {
        savingPermissions.value = true
        await permissionsService.syncUserPermissions(user.value.id, selectedPermissions.value)
        userPermissions.value = [...selectedPermissions.value]
        toastStore.addToast({ type: 'success', message: 'Permissions mises à jour avec succès' })
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des permissions:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la sauvegarde des permissions' })
      } finally {
        savingPermissions.value = false
      }
    }

    const saveUser = async () => {
      try {
        saving.value = true
        await usersService.updateUser(user.value.id, {
          name: editForm.name,
          email: editForm.email,
          role: user.value.role,
          active: user.value.active
        })
        user.value.name = editForm.name
        user.value.email = editForm.email
        showEditModal.value = false
        toastStore.addToast({ type: 'success', message: 'Utilisateur modifié avec succès' })
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la modification' })
      } finally {
        saving.value = false
      }
    }

    const changeRole = async () => {
      try {
        saving.value = true
        const newRole = user.value.role === 'admin' ? 'user' : 'admin'
        await usersService.changeUserRole(user.value.id, newRole)
        user.value.role = newRole
        showRoleModal.value = false
        toastStore.addToast({ type: 'success', message: newRole === 'admin' ? 'Promu administrateur' : 'Rôle changé en utilisateur standard' })

        if (newRole === 'user') {
          loadPermissions()
        }
      } catch (error) {
        console.error('Erreur lors du changement de rôle:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors du changement de rôle' })
      } finally {
        saving.value = false
      }
    }

    const toggleStatus = async () => {
      try {
        togglingStatus.value = true
        const newStatus = !user.value.active
        await usersService.toggleUserStatus(user.value.id, newStatus)
        user.value.active = newStatus
        showStatusConfirmModal.value = false
        toastStore.addToast({ type: 'success', message: newStatus ? 'Utilisateur activé' : 'Utilisateur désactivé' })
      } catch (error) {
        console.error('Erreur lors du changement de statut:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors du changement de statut' })
      } finally {
        togglingStatus.value = false
      }
    }

    const deleteUser = async () => {
      try {
        saving.value = true
        await usersService.deleteUser(user.value.id)
        toastStore.addToast({ type: 'success', message: 'Utilisateur supprimé' })
        router.push({ name: 'users' })
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la suppression' })
      } finally {
        saving.value = false
      }
    }

    const getInitials = (name) => {
      if (!name) return ''
      return name.split(' ').map(part => part.charAt(0)).join('').toUpperCase().substring(0, 2)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const formatPrice = (amount) => {
      if (amount === undefined || amount === null) return '0 XOF'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount)
    }

    // Watch for edit modal to reset form
    watch(showEditModal, (val) => {
      if (val) {
        editForm.name = user.value.name
        editForm.email = user.value.email
      }
    })

    // Auto-expand groups when searching
    watch(permissionSearch, (val) => {
      if (val) {
        expandedGroups.value = filteredPermissionGroups.value.map(g => g.name)
      }
    })

    onMounted(() => {
      loadUser()
    })

    return {
      user,
      loading,
      saving,
      togglingStatus,
      showEditModal,
      showRoleModal,
      showStatusConfirmModal,
      showDeleteModal,
      showClearAllModal,
      editForm,
      permissionsLoading,
      savingPermissions,
      availablePermissions,
      selectedPermissions,
      expandedGroups,
      permissionSearch,
      permissionGroups,
      filteredPermissionGroups,
      hasPermissionChanges,
      addedPermissions,
      removedPermissions,
      loadUser,
      toggleGroup,
      togglePermission,
      isGroupFullyChecked,
      isGroupPartiallyChecked,
      getGroupActiveCount,
      toggleGroupPermissions,
      resetPermissions,
      selectAllPermissions,
      deselectAllPermissions,
      clearAllPermissions,
      isPermissionAdded,
      isPermissionRemoved,
      getPermissionItemClass,
      hasGroupChanges,
      savePermissions,
      saveUser,
      changeRole,
      toggleStatus,
      deleteUser,
      getInitials,
      formatDate,
      formatPrice
    }
  }
})
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div {
  transition: transform 0.2s ease;
}
.modal-leave-active {
  transition: opacity 0.15s ease;
}
.modal-leave-active > div {
  transition: transform 0.15s ease;
}
.modal-enter-from {
  opacity: 0;
}
.modal-enter-from > div {
  transform: scale(0.95) translateY(-8px);
}
.modal-leave-to {
  opacity: 0;
}
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}

input[type="checkbox"]:indeterminate {
  background-color: #0D9488;
  border-color: #0D9488;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 16 16'%3e%3cpath stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8h8'/%3e%3c/svg%3e");
}
</style>
